import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import {
  fetchCurrentUser,
  login as apiLogin,
  logout as apiLogout,
  type LoginResult,
} from "../lib/api";
import type { AuthUser } from "../lib/roles";

/**
 * Контекст роли/сессии (SK-5). При старте спрашивает у api текущего пользователя по
 * cookie-сессии (Р-5) и раздаёт его вниз по дереву. Роли отсюда управляют видимостью
 * навигации; настоящий запрет операций — на сервере (матрица 4.3, E1).
 */

export type AuthStatus = "loading" | "authenticated" | "anonymous";

export interface AuthContextValue {
  readonly status: AuthStatus;
  readonly user: AuthUser | null;
  /** Вход: при успехе поднимает сессию в контексте; результат отдаётся форме для сообщений. */
  readonly login: (login: string, password: string) => Promise<LoginResult>;
  /** Выход: гасит серверную сессию и забывает пользователя. */
  readonly logout: () => Promise<void>;
  /**
   * Перечитывает текущего пользователя с сервера (D-15). Нужен там, где состояние сессии
   * меняется НЕ входом и не выходом: после смены своего пароля снимается флаг
   * `mustChangePassword`, и без перечитывания интерфейс продолжал бы держать человека
   * на экране смены пароля. Сервер здесь единственный источник истины — клиент не
   * «подправляет» флаг у себя.
   */
  readonly refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [user, setUser] = useState<AuthUser | null>(null);

  useEffect(() => {
    let active = true;
    void fetchCurrentUser().then((current) => {
      if (!active) return;
      setUser(current);
      setStatus(current ? "authenticated" : "anonymous");
    });
    return () => {
      active = false;
    };
  }, []);

  const login = useCallback(async (loginValue: string, password: string) => {
    const result = await apiLogin(loginValue, password);
    if (result.ok) {
      setUser(result.user);
      setStatus("authenticated");
    }
    return result;
  }, []);

  const logout = useCallback(async () => {
    await apiLogout();
    setUser(null);
    setStatus("anonymous");
  }, []);

  const refresh = useCallback(async () => {
    const current = await fetchCurrentUser();
    setUser(current);
    setStatus(current ? "authenticated" : "anonymous");
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ status, user, login, logout, refresh }),
    [status, user, login, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Доступ к сессии/роли. Бросает, если вызван вне <AuthProvider> (ошибка сборки дерева). */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth должен использоваться внутри <AuthProvider>.");
  return ctx;
}
